import React, { useMemo, useState } from "react";
import { compose, isNil } from "ramda";
import basePDFViewer from "../../../../hoc/base-pdf-viewer";
import { MODALS, POINTER_MODES } from "../../../../constants/editor";
import SavePointModal from "../save-point-modal";
import {
  addPoint,
  setActiveObjectId,
} from "../../../../reducers/editor/actions";
import { useDispatch, useSelector } from "react-redux";
import BaseEditor from "../../../../components/base-editor";
import {
  selectCurrentPointerMode,
  selectRectangles,
} from "../../../../reducers/editor/selectors";
import {
  selectCanvas,
  selectPage,
} from "../../../../reducers/pdf-viewer/selectors";

import classes from "./pdf-editor.module.scss";
import { generateRectangleField } from "../../../../helpers/objectGenerators";
import { setDraftingObject } from "../../../../reducers/project/actions";
import { OBJECT_TYPES } from "../../../../constants/project";

const PDFEditor = ({
  width,
  height,
  cursorTopPdf,
  cursorLeftPdf,
  pageRef,
  position,
  convertXToPdfUnits,
  convertYToPdfUnits,
}) => {
  const dispatch = useDispatch();
  const currentPage = useSelector(selectPage);
  const currentPointerMode = useSelector(selectCurrentPointerMode);
  const page = useSelector(selectPage);
  const canvas = useSelector(selectCanvas);
  const rectangles = useSelector(selectRectangles);

  const [modalShow, setModalShow] = useState(null);

  const onSavePointData = (data) => {
    setModalShow(null);
    dispatch(
      addPoint({
        x: cursorLeftPdf,
        y: cursorTopPdf,
        page: currentPage,
        comment: data.comment,
      })
    );
  };

  const handleMouseDownFunc = () => {
    dispatch(setActiveObjectId(null));
  };

  const isValidNumber = (value) => {
    return !isNil(value) && !isNaN(value);
  };

  const handleMouseUpFunc = (mouseDownPositionX, mouseDownPositionY, x, y) => {
    const topLeftCornerX = Math.min(position.x, mouseDownPositionX);
    const topLeftCornerY = Math.min(position.y, mouseDownPositionY);
    const bottomRightCornerX = Math.max(position.x, mouseDownPositionX);
    const bottomRightCornerY = Math.max(position.y, mouseDownPositionY);

    if (
      !isValidNumber(topLeftCornerX) ||
      !isValidNumber(topLeftCornerY) ||
      !isValidNumber(bottomRightCornerX) ||
      !isValidNumber(bottomRightCornerY)
    ) {
      return;
    }

    const isPoint = topLeftCornerX === bottomRightCornerX && topLeftCornerY === bottomRightCornerY;
    const rectangleObject = generateRectangleField(rectangles, {
      topLeftCornerX: convertXToPdfUnits(
        topLeftCornerX +
          pageRef.current.parentElement.scrollLeft -
          canvas.canvasMarginLeft
      ),
      topLeftCornerY: convertYToPdfUnits(
        topLeftCornerY + pageRef.current.parentElement.scrollTop
      ),
      width: isPoint ? null : convertXToPdfUnits(bottomRightCornerX - topLeftCornerX),
      height: isPoint ? null : convertYToPdfUnits(bottomRightCornerY - topLeftCornerY),
      page: page - 1,
    });

    dispatch(
      setDraftingObject({
        ...rectangleObject,
        data: { objectType: OBJECT_TYPES.ANNOTATION },
      })
    );
    dispatch(setActiveObjectId(rectangleObject.id));
  };

  const cursorClass = useMemo(() => {
    switch (currentPointerMode) {
      case POINTER_MODES.POSITION:
        return classes.PositionCursor;
      case POINTER_MODES.SELECTION:
        return classes.SelectionCursor;
    }
  }, [currentPointerMode]);

  return (
    <div>
      <BaseEditor
        handleMouseUpFunc={handleMouseUpFunc}
        handleMouseDownFunc={handleMouseDownFunc}
        convertXToPdfUnits={convertXToPdfUnits}
        convertYToPdfUnits={convertYToPdfUnits}
        position={position}
        height={height}
        width={width}
        cursorClass={cursorClass}
        pageRef={pageRef}
      />
      {modalShow === MODALS.savePointModal && (
        <SavePointModal
          isOpen={modalShow === MODALS.savePointModal}
          handleHide={() => setModalShow(null)}
          onSubmit={onSavePointData}
        />
      )}
    </div>
  );
};

export default compose(basePDFViewer)(PDFEditor);
